import React, { useContext, useState } from 'react';

import { NoPermissionToAddIssueTypeModal } from '@qality/common-components/src/NoPermissionToAddIssueTypeModal';
import { HelpPanel } from '@qality/common-components/src/molecules/HelpPanel';
import { ImageTextCard } from '@qality/common-components/src/molecules/HelpPanel/ImageTextCard';
import { FEATURE_FLAG, IS_CLOUD } from '@qality/common-config';
import { DashboardContext } from '@qality/common-pages/src/Dashboard/DashboardProvider';
import { StorageOfUniqueServices } from '@qality/common-service';
import { CreateTestCycleDialogRenderer } from '@qality/server-renderers';

export const DashboardOnboarding: React.FC = () => {
  const ImageService = StorageOfUniqueServices.get('ImageService');
  const {
    isProjectAdmin,
    isQAlityConfigured,
    isOnboardingGadgetOpened,
    closeOnboardingGadget,
  } = useContext(DashboardContext);
  const [showNoPermissionModal, setShowNoPermissionModal] = useState(false);

  const handleOpenDialog = () => {
    if (IS_CLOUD) {
      window.AP.dialog.create({
        key: 'create-test-cycle-dialog',
        size: 'maximum',
        chrome: false,
      });
    } else {
      CreateTestCycleDialogRenderer.openCreateTestCycleDialog();
    }
  };

  const goToSettings = () => {
    if (isProjectAdmin) {
      window.open(
        StorageOfUniqueServices.get('URLService').getSettingsUrl(),
        '_blank'
      );
    } else {
      setShowNoPermissionModal(true);
    }
  };

  const cards = [
    ...(!isQAlityConfigured
      ? [
          {
            name: 'Configure issue types',
            content: (
              <ImageTextCard
                imageLink={ImageService.getAddIssueTypePath()}
                text='To start using QAlity Plus your Jira Administrator needs to add the QAlity Test issue type to your projects. QAlity will store the test data in this issue.'
              />
            ),
            mainAction: {
              label: 'Add issue type',
              callback: goToSettings,
            },
            secondaryAction: {
              label: 'Learn more',
              link: 'https://soldevelo.atlassian.net/wiki/spaces/QS/pages/1729560586/Quick+start',
            },
          },
        ]
      : []),
    {
      name: 'Create test case',
      content: (
        <ImageTextCard
          imageLink={ImageService.getAddTestCasePath()}
          text='A test case is a definition of all the necessary steps, inputs, conditions, and expected results that allow you to test a given feature. You can create test cases inside every issue page.'
        />
      ),
      secondaryAction: {
        label: 'Learn more about test cases',
        link: 'https://soldevelo.atlassian.net/wiki/spaces/QS/pages/1729560626/Creating+test+cases',
      },
    },
    {
      name: 'Execute test case',
      content: (
        <ImageTextCard
          imageLink={ImageService.getExecuteTestCasePath()}
          text='Start test execution from Jira issue page. Walk through test steps, report bugs and manage test status.'
        />
      ),
      secondaryAction: {
        label: 'Learn more',
        link: 'https://soldevelo.atlassian.net/wiki/spaces/QS/pages/1729626152/Execute+Test+Case',
      },
    },
    {
      name: 'Manage testing process in test cycles',
      content: (
        <ImageTextCard
          imageLink={ImageService.getManageInTestCyclePath()}
          text='To make your testing process efficient, you can organize test cases into cycles and increase your process transparency'
        />
      ),
      mainAction: {
        label: 'Create test cycle',
        callback: handleOpenDialog,
      },
      secondaryAction: {
        label: 'Learn more',
        link: 'https://soldevelo.atlassian.net/wiki/spaces/QS/pages/2005762132/Test+Cycles+Overview',
      },
    },
    {
      name: 'Enhance transparency with reports',
      content: (
        <ImageTextCard
          imageLink={ImageService.getReportingPath()}
          text='We believe in the transparency of software testing. QAlity Plus comes with reports that help you track the process and deliver high-quality products.'
        />
      ),
      secondaryAction: {
        label: 'Learn more about reports',
        link: 'https://soldevelo.atlassian.net/wiki/spaces/QS/pages/2358935602/Reports',
      },
    },
    {
      name: 'Find help if needed',
      content: (
        <ImageTextCard text='If you face any difficulties, we are here to help. You can always contact our Customer Care Specialists or browse documentation for answers.' />
      ),
      mainAction: {
        label: 'Contact support',
        link: 'https://soldevelo.atlassian.net/servicedesk/customer/portal/6',
      },
      secondaryAction: {
        label: 'Explore documentation',
        link: 'https://soldevelo.atlassian.net/wiki/spaces/QS/overview',
      },
    },
  ];

  return (
    <>
      {isOnboardingGadgetOpened && (
        <HelpPanel
          header={
            'Quick start with QAlity' + (FEATURE_FLAG.isPremium ? ' Plus' : '')
          }
          onClose={closeOnboardingGadget}
          cards={cards}
        />
      )}
      {isOnboardingGadgetOpened && showNoPermissionModal && (
        <NoPermissionToAddIssueTypeModal
          onClose={() => setShowNoPermissionModal(false)}
          showModal={showNoPermissionModal}
        />
      )}
    </>
  );
};
